import React, { useState, useEffect } from 'react';
import { ShoppingBag, Search, Plus, Eye, Edit, Trash2, Check, X, Package, Calendar, Truck, Clock, CheckCircle, XCircle, AlertCircle, DollarSign } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import OrdenCompraForm from './OrdenCompraForm';
import OrdenCompraDetalle from './OrdenCompraDetalle';
import RecepcionModal from './RecepcionModal';
import apiService from '../../services/apiService';
import { formatMoney, formatDate } from '../../utils/formatters';
import './OrdenesCompraPage.css';

const estadoConfig = {
    borrador: { label: 'Borrador', icon: Edit, color: 'gray' },
    pendiente: { label: 'Pendiente', icon: Clock, color: 'warning' },
    aprobada: { label: 'Aprobada', icon: CheckCircle, color: 'info' },
    recibida_parcial: { label: 'Parcial', icon: AlertCircle, color: 'warning' },
    recibida: { label: 'Recibida', icon: Package, color: 'success' },
    cancelada: { label: 'Cancelada', icon: XCircle, color: 'error' }
};

const OrdenesCompraPage = () => {
    const { loading, setLoading, showToast, user } = useApp();

    const [ordenes, setOrdenes] = useState([]);
    const [filteredOrdenes, setFilteredOrdenes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterEstado, setFilterEstado] = useState('todos');

    // Fechas por defecto: mes en curso
    const getDefaultDates = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
            inicio: firstDay.toISOString().split('T')[0],
            fin: lastDay.toISOString().split('T')[0]
        };
    };

    const defaultDates = getDefaultDates();
    const [fechaInicio, setFechaInicio] = useState(defaultDates.inicio);
    const [fechaFin, setFechaFin] = useState(defaultDates.fin);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showRecepcionModal, setShowRecepcionModal] = useState(false);
    const [selectedOrden, setSelectedOrden] = useState(null);
    const [proveedores, setProveedores] = useState([]);

    useEffect(() => {
        loadOrdenes();
        loadProveedores();
    }, []);

    useEffect(() => {
        filterOrdenes();
    }, [ordenes, searchQuery, filterEstado, fechaInicio, fechaFin]);

    const loadOrdenes = async () => {
        try {
            setLoading(true);
            const result = await apiService.getOrdenesCompra();
            if (result.success) {
                setOrdenes(result.data || []);
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al cargar órdenes de compra', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadProveedores = async () => {
        try {
            const result = await apiService.getProveedores();
            if (result.success) {
                setProveedores(result.data || []);
            }
        } catch (error) {
            console.error('Error al cargar proveedores:', error);
        }
    };

    const filterOrdenes = () => {
        let filtered = ordenes;

        // Filtro por búsqueda
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(orden =>
                orden.folio?.toLowerCase().includes(query) ||
                orden.proveedor_nombre?.toLowerCase().includes(query)
            );
        }

        // Filtro por estado
        if (filterEstado !== 'todos') {
            filtered = filtered.filter(orden => orden.estado === filterEstado);
        }

        // Filtro por fecha inicio
        if (fechaInicio) {
            const inicio = new Date(fechaInicio);
            inicio.setHours(0, 0, 0, 0);
            filtered = filtered.filter(orden => {
                const fechaOrden = new Date(orden.fecha_creacion);
                return fechaOrden >= inicio;
            });
        }

        // Filtro por fecha fin
        if (fechaFin) {
            const fin = new Date(fechaFin);
            fin.setHours(23, 59, 59, 999);
            filtered = filtered.filter(orden => {
                const fechaOrden = new Date(orden.fecha_creacion);
                return fechaOrden <= fin;
            });
        }

        setFilteredOrdenes(filtered);
    };

    const clearDateFilters = () => {
        const defaults = getDefaultDates();
        setFechaInicio(defaults.inicio);
        setFechaFin(defaults.fin);
    };

    const handleCreateOrden = async (ordenData) => {
        try {
            setLoading(true);
            const dataConUsuario = {
                ...ordenData,
                usuario_id: user?.user_id || null
            };
            const result = await apiService.crearOrdenCompra(dataConUsuario);
            if (result.success) {
                setShowCreateModal(false);
                loadOrdenes();
                showToast('Orden de compra creada exitosamente', 'success');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al crear orden de compra', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditOrden = async (ordenData) => {
        try {
            setLoading(true);
            const result = await apiService.actualizarOrdenCompra({
                id: selectedOrden.id,
                ...ordenData
            });
            if (result.success) {
                setShowEditModal(false);
                setSelectedOrden(null);
                loadOrdenes();
                showToast('Orden actualizada exitosamente', 'success');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al actualizar orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOrden = async (id, folio) => {
        if (!window.confirm(`¿Estás seguro de eliminar la orden "${folio}"?`)) {
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.eliminarOrdenCompra(id);
            if (result.success) {
                loadOrdenes();
                showToast('Orden eliminada exitosamente', 'success');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al eliminar orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEnviarAprobacion = async (id) => {
        try {
            setLoading(true);
            const result = await apiService.enviarAprobacionOrden(id);
            if (result.success) {
                loadOrdenes();
                showToast('Orden enviada a aprobación', 'success');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al enviar orden a aprobación', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAprobarOrden = async (id) => {
        try {
            setLoading(true);
            const result = await apiService.aprobarOrdenCompra(id, user?.user_id || null);
            if (result.success) {
                loadOrdenes();
                showToast('Orden aprobada exitosamente', 'success');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al aprobar orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRechazarOrden = async (id) => {
        const motivo = window.prompt('Motivo del rechazo:');
        if (motivo === null) return;

        try {
            setLoading(true);
            const result = await apiService.rechazarOrdenCompra(id, motivo, user?.user_id || null);
            if (result.success) {
                loadOrdenes();
                showToast('Orden rechazada', 'warning');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al rechazar orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelarOrden = async (id) => {
        if (!window.confirm('¿Estás seguro de cancelar esta orden?')) {
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.cancelarOrdenCompra(id);
            if (result.success) {
                loadOrdenes();
                showToast('Orden cancelada', 'warning');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al cancelar orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = async (orden) => {
        try {
            setLoading(true);
            const result = await apiService.getOrdenCompra(orden.id);
            if (result.success) {
                setSelectedOrden(result.data);
                setShowDetailModal(true);
            } else {
                showToast('Error al cargar detalle de orden', 'error');
            }
        } catch (error) {
            showToast('Error al cargar detalle de orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenRecepcion = async (orden) => {
        try {
            setLoading(true);
            const result = await apiService.getOrdenCompra(orden.id);
            if (result.success) {
                setSelectedOrden(result.data);
                setShowRecepcionModal(true);
            } else {
                showToast('Error al cargar orden', 'error');
            }
        } catch (error) {
            showToast('Error al cargar orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRecibirOrden = async (productosRecibidos, estadoPago = 'pendiente') => {
        try {
            setLoading(true);
            const result = await apiService.recibirOrdenCompra(selectedOrden.id, productosRecibidos, user?.user_id || null, estadoPago);
            if (result.success) {
                setShowRecepcionModal(false);
                setSelectedOrden(null);
                loadOrdenes();
                showToast('Productos recibidos e inventario actualizado', 'success');
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al registrar recepción', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEdit = async (orden) => {
        try {
            setLoading(true);
            const result = await apiService.getOrdenCompra(orden.id);
            if (result.success) {
                setSelectedOrden(result.data);
                setShowEditModal(true);
            } else {
                showToast('Error al cargar orden', 'error');
            }
        } catch (error) {
            showToast('Error al cargar orden', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getEstadoBadge = (estado) => {
        const config = estadoConfig[estado] || estadoConfig.borrador;
        const Icon = config.icon;
        return (
            <span className={`status-badge status-${config.color}`}>
                <Icon size={12} />
                {config.label}
            </span>
        );
    };

    const getAccionesDisponibles = (orden) => {
        const acciones = [];

        // Ver detalle siempre disponible
        acciones.push({
            icon: Eye,
            label: 'Ver',
            onClick: () => handleViewDetail(orden)
        });

        switch (orden.estado) {
            case 'borrador':
                acciones.push(
                    { icon: Edit, label: 'Editar', onClick: () => handleOpenEdit(orden) },
                    { icon: Check, label: 'Enviar', onClick: () => handleEnviarAprobacion(orden.id), className: 'success' },
                    { icon: Trash2, label: 'Eliminar', onClick: () => handleDeleteOrden(orden.id, orden.folio), className: 'delete' }
                );
                break;
            case 'pendiente':
                acciones.push(
                    { icon: Check, label: 'Aprobar', onClick: () => handleAprobarOrden(orden.id), className: 'success' },
                    { icon: X, label: 'Rechazar', onClick: () => handleRechazarOrden(orden.id), className: 'delete' }
                );
                break;
            case 'aprobada':
            case 'recibida_parcial':
                acciones.push(
                    { icon: Package, label: 'Recibir', onClick: () => handleOpenRecepcion(orden), className: 'success' },
                    { icon: X, label: 'Cancelar', onClick: () => handleCancelarOrden(orden.id), className: 'delete' }
                );
                break;
            default:
                break;
        }

        return acciones;
    };

    return (
        <div className="ordenes-compra-page">
            {/* Controles */}
            <div className="ordenes-controls">
                <div className="search-filters">
                    <div className="search-container">
                        <Input
                            placeholder="Buscar por folio o proveedor..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={<Search size={20} />}
                        />
                    </div>
                    <div className="date-filters">
                        <div className="date-input-group">
                            <label>Desde:</label>
                            <input
                                type="date"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                                className="date-input"
                            />
                        </div>
                        <div className="date-input-group">
                            <label>Hasta:</label>
                            <input
                                type="date"
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.target.value)}
                                className="date-input"
                            />
                        </div>
                        {(fechaInicio || fechaFin) && (
                            <button
                                className="clear-dates-btn"
                                onClick={clearDateFilters}
                                title="Limpiar fechas"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <div className="filter-buttons">
                        <button
                            className={`filter-btn ${filterEstado === 'todos' ? 'active' : ''}`}
                            onClick={() => setFilterEstado('todos')}
                        >
                            Todos
                        </button>
                        <button
                            className={`filter-btn ${filterEstado === 'borrador' ? 'active' : ''}`}
                            onClick={() => setFilterEstado('borrador')}
                        >
                            <Edit size={14} />
                            Borrador
                        </button>
                        <button
                            className={`filter-btn ${filterEstado === 'pendiente' ? 'active' : ''}`}
                            onClick={() => setFilterEstado('pendiente')}
                        >
                            <Clock size={14} />
                            Pendiente
                        </button>
                        <button
                            className={`filter-btn ${filterEstado === 'aprobada' ? 'active' : ''}`}
                            onClick={() => setFilterEstado('aprobada')}
                        >
                            <CheckCircle size={14} />
                            Aprobada
                        </button>
                        <button
                            className={`filter-btn ${filterEstado === 'recibida' ? 'active' : ''}`}
                            onClick={() => setFilterEstado('recibida')}
                        >
                            <Package size={14} />
                            Recibida
                        </button>
                    </div>
                </div>
                <div className="controls-actions">
                    <Button onClick={() => setShowCreateModal(true)} disabled={loading}>
                        <Plus size={18} />
                        Nueva Orden
                    </Button>
                </div>
            </div>

            {/* Tabla */}
            <div className="ordenes-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando órdenes...</p>
                    </div>
                )}

                {!loading && filteredOrdenes.length === 0 && (
                    <div className="empty-state">
                        <ShoppingBag size={48} />
                        <h3>No hay órdenes de compra</h3>
                        <p>
                            {searchQuery
                                ? 'No hay órdenes que coincidan con la búsqueda'
                                : 'Crea tu primera orden de compra'
                            }
                        </p>
                        {!searchQuery && (
                            <Button onClick={() => setShowCreateModal(true)}>

                                Nueva Orden
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredOrdenes.length > 0 && (
                    <div className="ordenes-table-wrapper">
                        <table className="ordenes-table">
                            <thead>
                                <tr>
                                    <th>Folio</th>
                                    <th>Fecha</th>
                                    <th>Proveedor</th>
                                    <th>Productos</th>
                                    <th>Total</th>
                                    <th>Estado</th>
                                    <th>Pago</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrdenes.map((orden) => (
                                    <tr key={orden.id}>
                                        <td>
                                            <div className="orden-folio">
                                                <ShoppingBag size={16} />
                                                <span>{orden.folio}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="orden-fecha">
                                                <Calendar size={14} />
                                                <span>{formatDate(orden.fecha_creacion)}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="orden-proveedor">
                                                <Truck size={14} />
                                                <span>{orden.proveedor_nombre || 'Sin proveedor'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="orden-productos">
                                                <Package size={14} />
                                                <span>{orden.total_productos || 0} productos</span>
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            <span className="orden-total">{formatMoney(orden.total)}</span>
                                        </td>
                                        <td>
                                            {getEstadoBadge(orden.estado)}
                                        </td>
                                        <td>
                                            {(orden.estado === 'recibida' || orden.estado === 'recibida_parcial') ? (
                                                <span className={`pago-badge ${orden.estado_pago === 'pagada' ? 'pagada' : 'pendiente'}`}>

                                                    {orden.estado_pago === 'pagada' ? 'Pagada' : 'Pendiente'}
                                                </span>
                                            ) : (
                                                <span className="pago-badge na">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="actions-compact">
                                                {getAccionesDisponibles(orden).map((accion, index) => (
                                                    <button
                                                        key={index}
                                                        className={`action-btn-compact ${accion.className || ''}`}
                                                        onClick={accion.onClick}
                                                        title={accion.label}
                                                    >
                                                        <accion.icon size={14} />
                                                    </button>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal Crear */}
            {showCreateModal && (
                <Modal
                    title="Nueva Orden de Compra"
                    onClose={() => setShowCreateModal(false)}
                    size="large"
                >
                    <OrdenCompraForm
                        proveedores={proveedores}
                        onSubmit={handleCreateOrden}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {/* Modal Editar */}
            {showEditModal && selectedOrden && (
                <Modal
                    title={`Editar Orden - ${selectedOrden.folio}`}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedOrden(null);
                    }}
                    size="large"
                >
                    <OrdenCompraForm
                        orden={selectedOrden}
                        proveedores={proveedores}
                        onSubmit={handleEditOrden}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedOrden(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}

            {/* Modal Detalle */}
            {showDetailModal && (
                <Modal
                    title={`Detalle - ${selectedOrden?.folio || ''}`}
                    onClose={() => {
                        setShowDetailModal(false);
                        setSelectedOrden(null);
                    }}
                    size="large"
                >
                    <OrdenCompraDetalle
                        orden={selectedOrden}
                        loading={loading}
                        showRecibido={true}
                        onClose={() => {
                            setShowDetailModal(false);
                            setSelectedOrden(null);
                        }}
                    />
                </Modal>
            )}

            {/* Modal Recepción */}
            {showRecepcionModal && selectedOrden && (
                <RecepcionModal
                    orden={selectedOrden}
                    onRecibir={handleRecibirOrden}
                    onClose={() => {
                        setShowRecepcionModal(false);
                        setSelectedOrden(null);
                    }}
                    isLoading={loading}
                />
            )}
        </div>
    );
};

export default OrdenesCompraPage;
